import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  GraduationCap, Award, DollarSign, Users, MessageSquare, Mail, Phone,
  Share2, Facebook, Instagram, Linkedin, Copy, Check, ChevronRight,
  Play, BookOpen, Target, Sparkles, ArrowRight, Building, Star,
  Megaphone, Link2, BarChart3, Gift, TrendingUp, CheckCircle, Lightbulb,
  MessageCircle, Globe, Zap, Video, ClipboardList
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { useAuth } from '../lib/auth';
import { cn } from '../lib/utils';

// Training modules
const TRAINING_MODULES = [
  {
    id: 'recommend',
    title: 'Part 1: Recommend Businesses',
    subtitle: '$120K Grant & Best of Best Program',
    icon: Award,
    color: 'amber',
    description: 'Learn how to recommend local businesses for the Co-Op Grant and Best of Best recognition'
  },
  {
    id: 'promote',
    title: 'Part 2: Promote & Earn',
    subtitle: 'Affiliate Strategies & Dashboard',
    icon: Megaphone,
    color: 'purple',
    description: 'Master promoting businesses, using your affiliate dashboard, and maximizing earnings'
  }
];

// Scripts data from the PDF
const SCRIPTS = {
  textMessage: [
    {
      title: 'Friendly & Local',
      script: `Hey [Name]! I'm helping One Force Live nominate and pre-approve local businesses to be Voted Best of Best and get up to $120,000 in Co-Op Grant Funding. If you own a business (or know one that deserves recognition), apply here: [YOUR LINK]. It's free and takes less than a minute!`
    },
    {
      title: 'For Friends to Share',
      script: `Hey! I'm helping One Force Live find amazing local businesses who should be Voted Best of Best and qualify for $120K in Co-Op Grant Funding. Can you share this link with any business owners you know? [YOUR LINK]. It really helps support our community!`
    },
    {
      title: 'Direct to Business Owner',
      script: `Hey there! I'd love to recommend your business for One Force Live's Voted Best of Best program. You may qualify for up to $120K in Co-Op Grant Funding to boost your marketing and growth. Quick link to apply: [YOUR LINK]`
    }
  ],
  email: {
    subjects: [
      'You Deserve to Be Voted Best of Best',
      'A Business Recommendation for You',
      'Nominate Your Business for Best of Best + $120K in Support'
    ],
    body: `Hi [Name],

I'm reaching out because One Force Live is currently recommending local businesses to be Voted Best of Best and to qualify for up to $120,000 in Co-Op Grant Funding to help with marketing, automation, and AI technology.

This program helps business owners dramatically grow their visibility and streamline operations with tools that would normally cost tens of thousands per month.

It takes about a minute to complete the quick pre-approval form: [YOUR LINK]

If you know another business that deserves recognition, feel free to share the link with them as well.

Warm regards,
[Your Name]
[Your Email]

Referral Link: [YOUR LINK]`
  },
  voicemail: `Hi, this is [Your Name]! I'm reaching out because I'm helping recommend local businesses to be Voted Best of Best and to qualify for up to $120,000 in Co-Op Grant Funding through One Force Live.

If you own a business or know someone who does - it takes less than a minute to check eligibility. Just visit the link in my message: [YOUR LINK]

It's completely free to apply, and we're helping 50 local businesses get approved this month.

This is [Your Name] with One Force Live - hope you get a chance to apply!`,
  messenger: [
    {
      title: 'Community Focused',
      script: `Hey [Name]! I'm helping local businesses get Voted Best of Best and qualify for up to $120K in Co-Op Grant Funding through One Force Live! Here's the quick pre-approval link [YOUR LINK]. It's free and fast - want me to explain how it works?`
    },
    {
      title: 'Short & Direct',
      script: `Hey! I'm helping local businesses get up to $120,000 in Co-Op Grant Funding and be recognized as Voted Best of Best. Apply here: [YOUR LINK]`
    },
    {
      title: 'For Supporters',
      script: `Hey! I'm helping 50 amazing businesses get recognized as Voted Best of Best and receive up to $120K in support. Can you share this link with your favorite local businesses? [YOUR LINK]`
    }
  ],
  followUp: `It's a program by One Force Live that supports small businesses with AI-powered marketing, automation systems, cinematic ads, and up to $120,000 in Co-Op Grant Funding.`,
  social: {
    facebook: `Hey friends! 🙌 I'm helping support local businesses by recommending them to be Voted Best of Best and qualify for up to $120,000 in Co-Op Grant Funding through One Force Live!

If you know a business that deserves recognition - a restaurant, realtor, roofer, boutique, salon, or service company - share this link: [YOUR LINK]

Let's help the Best of the Best shine! ⭐`,
    instagram: `Support Local Businesses! 🏆 Nominate them for Voted Best of Best + $120K in Co-Op Grant Funding to grow with AI-powered tools!

Apply here: [YOUR LINK]

#BestOfBest #SupportLocal #OneForceLive`,
    linkedin: `Hi everyone 👋

I'm helping nominate outstanding local businesses to be recognized as Voted Best of Best and to receive up to $120,000 in Co-Op Grant Funding through One Force Live.

If you know a business that stands out for quality, service, or community impact, share this link with them: [YOUR LINK]

Let's help business leaders access the tools and resources they need to grow and scale.`
  }
};

function CopyButton({ text, label = 'Copy' }) {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };
  
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={handleCopy}
      className="border-white/20 text-xs"
    >
      {copied ? (
        <><Check className="w-3 h-3 mr-1 text-green-400" /> Copied!</>
      ) : (
        <><Copy className="w-3 h-3 mr-1" /> {label}</>
      )}
    </Button>
  );
}

function ScriptCard({ title, script, icon: Icon }) {
  return (
    <div className="bg-white/5 rounded-xl p-4 border border-white/10 hover:border-cyan-500/30 transition-all">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4 text-cyan-400" />}
          <h4 className="font-medium text-white text-sm">{title}</h4>
        </div>
        <CopyButton text={script} />
      </div>
      <p className="text-sm text-gray-400 whitespace-pre-wrap leading-relaxed">{script}</p>
    </div>
  );
}

function Part1RecommendTraining() {
  const { user } = useAuth();
  const affiliateLink = user?.affiliate_link || '[YOUR AFFILIATE LINK]';
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-8"
    >
      {/* Mission Overview */}
      <div className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 rounded-2xl p-6 border border-amber-500/20">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center flex-shrink-0">
            <Target className="w-6 h-6 text-amber-400" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-white mb-2">Mission Overview</h3>
            <p className="text-gray-300 leading-relaxed">
              Welcome to the One Force Live Affiliate Team! Your mission is to help local businesses 
              get recognized as <span className="text-amber-400 font-semibold">Voted Best of Best</span> and 
              qualify for up to <span className="text-green-400 font-semibold">$120,000 in Co-Op Grant Funding</span>.
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <span className="px-3 py-1 bg-amber-500/20 text-amber-400 rounded-full text-sm">AI-Powered Marketing</span>
              <span className="px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-sm">$120K Grant Funding</span>
              <span className="px-3 py-1 bg-purple-500/20 text-purple-400 rounded-full text-sm">Automation Systems</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Key Phrases */}
      <div className="bg-[#0d1525] rounded-xl p-6 border border-white/10">
        <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
          <Lightbulb className="w-5 h-5 text-yellow-400" />
          Key Phrases to Remember
        </h3>
        <div className="grid sm:grid-cols-2 gap-3">
          {[
            '"Voted Best of Best"',
            '"Up to $120,000 in Co-Op Grant Funding"',
            '"AI Horse-Powered marketing technology"',
            '"Free and takes less than a minute"',
            '"Support our community"',
            '"Help the Best of the Best shine"'
          ].map((phrase, i) => (
            <div key={i} className="flex items-center gap-2 p-3 bg-white/5 rounded-lg">
              <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
              <span className="text-sm text-gray-300">{phrase}</span>
            </div>
          ))}
        </div>
      </div>
      
      {/* Text Message Scripts */}
      <div className="bg-[#0d1525] rounded-xl p-6 border border-white/10">
        <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-green-400" />
          Text Message Scripts
        </h3>
        <div className="space-y-4">
          {SCRIPTS.textMessage.map((item, i) => (
            <ScriptCard 
              key={i} 
              title={item.title} 
              script={item.script.replace('[YOUR LINK]', affiliateLink).replace('[Name]', '[Name]')} 
              icon={MessageSquare}
            />
          ))}
        </div>
      </div>
      
      {/* Email Script */}
      <div className="bg-[#0d1525] rounded-xl p-6 border border-white/10">
        <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
          <Mail className="w-5 h-5 text-blue-400" />
          Email Script
        </h3>
        <div className="mb-4">
          <p className="text-sm text-gray-500 mb-2">Subject Line Options:</p>
          <div className="flex flex-wrap gap-2">
            {SCRIPTS.email.subjects.map((subject, i) => (
              <span key={i} className="px-3 py-1 bg-blue-500/10 text-blue-400 rounded-full text-sm border border-blue-500/20">
                {subject}
              </span>
            ))}
          </div>
        </div>
        <ScriptCard 
          title="Email Body" 
          script={SCRIPTS.email.body.replace(/\[YOUR LINK\]/g, affiliateLink)} 
          icon={Mail}
        />
      </div>
      
      {/* Voicemail Script */}
      <div className="bg-[#0d1525] rounded-xl p-6 border border-white/10">
        <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
          <Phone className="w-5 h-5 text-purple-400" />
          Ringless Voicemail Script (35-40 seconds)
        </h3>
        <ScriptCard 
          title="Voicemail" 
          script={SCRIPTS.voicemail.replace('[YOUR LINK]', affiliateLink)} 
          icon={Phone}
        />
      </div>
      
      {/* Messenger Scripts */}
      <div className="bg-[#0d1525] rounded-xl p-6 border border-white/10">
        <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
          <MessageCircle className="w-5 h-5 text-cyan-400" />
          Messenger Scripts
        </h3>
        <div className="space-y-4">
          {SCRIPTS.messenger.map((item, i) => (
            <ScriptCard 
              key={i} 
              title={item.title} 
              script={item.script.replace('[YOUR LINK]', affiliateLink)} 
              icon={MessageCircle}
            />
          ))}
          <div className="p-4 bg-cyan-500/10 rounded-xl border border-cyan-500/20">
            <p className="text-sm text-cyan-400 font-medium mb-2">Follow-Up Response (if they ask "Tell me more"):</p>
            <p className="text-sm text-gray-300">{SCRIPTS.followUp}</p>
          </div>
        </div>
      </div>
      
      {/* Social Media Posts */}
      <div className="bg-[#0d1525] rounded-xl p-6 border border-white/10">
        <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
          <Globe className="w-5 h-5 text-pink-400" />
          Social Media Posts
        </h3>
        <div className="space-y-4">
          <ScriptCard 
            title="Facebook Post" 
            script={SCRIPTS.social.facebook.replace('[YOUR LINK]', affiliateLink)} 
            icon={Facebook}
          />
          <ScriptCard 
            title="Instagram Caption" 
            script={SCRIPTS.social.instagram.replace('[YOUR LINK]', affiliateLink)} 
            icon={Instagram}
          />
          <ScriptCard 
            title="LinkedIn Post" 
            script={SCRIPTS.social.linkedin.replace('[YOUR LINK]', affiliateLink)} 
            icon={Linkedin}
          />
        </div>
      </div>
    </motion.div>
  );
}

function Part2PromoteTraining() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-8"
    >
      {/* Overview */}
      <div className="bg-gradient-to-br from-purple-500/10 to-indigo-500/10 rounded-2xl p-6 border border-purple-500/20">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center flex-shrink-0">
            <TrendingUp className="w-6 h-6 text-purple-400" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-white mb-2">Maximize Your Earnings</h3>
            <p className="text-gray-300 leading-relaxed">
              Learn how to use your affiliate dashboard, promote businesses effectively, and 
              track your earnings. Every promotion you make helps local businesses grow while 
              earning you commissions and credits.
            </p>
          </div>
        </div>
      </div>
      
      {/* Using Your Affiliate Dashboard */}
      <div className="bg-[#0d1525] rounded-xl p-6 border border-white/10">
        <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-cyan-400" />
          Using Your Affiliate Dashboard
        </h3>
        <div className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="p-4 bg-white/5 rounded-xl border border-white/10">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center">
                  <Link2 className="w-5 h-5 text-cyan-400" />
                </div>
                <div>
                  <h4 className="font-medium text-white">Get Your Links</h4>
                  <p className="text-xs text-gray-500">Dashboard → My Promotions</p>
                </div>
              </div>
              <p className="text-sm text-gray-400">Each business you promote has a unique affiliate link. Copy and share these links to track conversions.</p>
            </div>
            
            <div className="p-4 bg-white/5 rounded-xl border border-white/10">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <h4 className="font-medium text-white">Track Earnings</h4>
                  <p className="text-xs text-gray-500">Dashboard → Earnings</p>
                </div>
              </div>
              <p className="text-sm text-gray-400">See your clicks, conversions, and total earnings in real-time. Monitor which promotions perform best.</p>
            </div>
            
            <div className="p-4 bg-white/5 rounded-xl border border-white/10">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                  <Megaphone className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <h4 className="font-medium text-white">Join Programs</h4>
                  <p className="text-xs text-gray-500">Affiliate Marketplace</p>
                </div>
              </div>
              <p className="text-sm text-gray-400">Browse the Affiliate Marketplace to find new businesses to promote. Click "Promote" to add them to your portfolio.</p>
            </div>
            
            <div className="p-4 bg-white/5 rounded-xl border border-white/10">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                  <Star className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <h4 className="font-medium text-white">Earn Credits</h4>
                  <p className="text-xs text-gray-500">Publishing Credits</p>
                </div>
              </div>
              <p className="text-sm text-gray-400">Earn publishing credits with each conversion. Use credits to publish your own content and boost visibility.</p>
            </div>
          </div>
          
          <Link to="/affiliate">
            <Button className="w-full bg-cyan-500 hover:bg-cyan-600 text-black font-semibold">
              <BarChart3 className="w-4 h-4 mr-2" /> Go to Affiliate Dashboard
            </Button>
          </Link>
        </div>
      </div>
      
      {/* Promotion Strategies */}
      <div className="bg-[#0d1525] rounded-xl p-6 border border-white/10">
        <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
          <Zap className="w-5 h-5 text-yellow-400" />
          Promotion Strategies That Work
        </h3>
        <div className="space-y-4">
          {[
            {
              title: 'Personal Recommendations',
              desc: 'Share businesses you personally use and trust. Authentic recommendations convert better.',
              icon: Users
            },
            {
              title: 'Local Community Focus',
              desc: 'Focus on businesses in your local area. People trust recommendations for local services.',
              icon: Building
            },
            {
              title: 'Social Proof',
              desc: 'Mention when a business is "Voted Best of Best" - it adds credibility to your recommendation.',
              icon: Award
            },
            {
              title: 'Story-Based Sharing',
              desc: 'Share your personal experience with the business. Stories are more engaging than just links.',
              icon: BookOpen
            },
            {
              title: 'Multi-Channel Approach',
              desc: 'Use text, email, social media, and in-person conversations. Different people prefer different channels.',
              icon: Globe
            },
            {
              title: 'Follow Up',
              desc: 'If someone shows interest, follow up within 24 hours. Most conversions happen after follow-up.',
              icon: MessageCircle
            }
          ].map((strategy, i) => (
            <div key={i} className="flex items-start gap-4 p-4 bg-white/5 rounded-xl">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-yellow-500/20 to-orange-500/20 flex items-center justify-center flex-shrink-0">
                <strategy.icon className="w-5 h-5 text-yellow-400" />
              </div>
              <div>
                <h4 className="font-medium text-white mb-1">{strategy.title}</h4>
                <p className="text-sm text-gray-400">{strategy.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Quick Actions */}
      <div className="bg-[#0d1525] rounded-xl p-6 border border-white/10">
        <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-cyan-400" />
          Quick Actions
        </h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <Link to="/affiliate/marketplace">
            <Button variant="outline" className="w-full justify-start border-white/20 h-auto py-3">
              <Megaphone className="w-5 h-5 mr-3 text-purple-400" />
              <div className="text-left">
                <p className="font-medium">Browse Marketplace</p>
                <p className="text-xs text-gray-500">Find businesses to promote</p>
              </div>
            </Button>
          </Link>
          <Link to="/affiliate">
            <Button variant="outline" className="w-full justify-start border-white/20 h-auto py-3">
              <BarChart3 className="w-5 h-5 mr-3 text-green-400" />
              <div className="text-left">
                <p className="font-medium">View Earnings</p>
                <p className="text-xs text-gray-500">Track your commissions</p>
              </div>
            </Button>
          </Link>
          <Link to="/ai-besty-link">
            <Button variant="outline" className="w-full justify-start border-white/20 h-auto py-3">
              <Award className="w-5 h-5 mr-3 text-amber-400" />
              <div className="text-left">
                <p className="font-medium">AI Besty Link</p>
                <p className="text-xs text-gray-500">Get your $60 referral link</p>
              </div>
            </Button>
          </Link>
          <Link to="/publishing">
            <Button variant="outline" className="w-full justify-start border-white/20 h-auto py-3">
              <Globe className="w-5 h-5 mr-3 text-cyan-400" />
              <div className="text-left">
                <p className="font-medium">Publish Content</p>
                <p className="text-xs text-gray-500">Use your credits to publish</p>
              </div>
            </Button>
          </Link>
        </div>
      </div>
      
      {/* Tips for Success */}
      <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 rounded-2xl p-6 border border-green-500/20">
        <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
          <CheckCircle className="w-5 h-5 text-green-400" />
          Tips for Success
        </h3>
        <ul className="space-y-3">
          {[
            'Be genuine - only promote businesses you believe in',
            'Personalize your messages - use names and specific details',
            'Focus on value - explain how the business helps people',
            'Be consistent - promote regularly, not just once',
            'Track what works - double down on successful strategies',
            'Engage with responses - answer questions promptly',
            'Celebrate wins - share success stories of businesses you helped'
          ].map((tip, i) => (
            <li key={i} className="flex items-start gap-3">
              <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
              <span className="text-sm text-gray-300">{tip}</span>
            </li>
          ))}
        </ul>
      </div>
    </motion.div>
  );
}

export default function AffiliateTrainingPage() {
  const [activeModule, setActiveModule] = useState('recommend');
  
  return (
    <DashboardLayout>
      <div className="p-6 pt-20 max-w-5xl mx-auto" data-testid="affiliate-training-page">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center mx-auto mb-4">
            <GraduationCap className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">My Affiliate Training</h1>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Master the art of recommending local businesses and promoting through the affiliate program. 
            These skills help you earn while supporting your community.
          </p>
        </motion.div>
        
        {/* Module Tabs */}
        <div className="flex flex-col sm:flex-row gap-3 mb-8">
          {TRAINING_MODULES.map((module) => (
            <button
              key={module.id}
              onClick={() => setActiveModule(module.id)}
              className={cn(
                "flex-1 p-4 rounded-xl border transition-all text-left",
                activeModule === module.id
                  ? module.color === 'amber' 
                    ? "bg-amber-500/10 border-amber-500/30" 
                    : "bg-purple-500/10 border-purple-500/30"
                  : "bg-white/5 border-white/10 hover:border-white/20"
              )}
            >
              <div className="flex items-center gap-3">
                <div className={cn(
                  "w-10 h-10 rounded-lg flex items-center justify-center",
                  module.color === 'amber' ? "bg-amber-500/20" : "bg-purple-500/20"
                )}>
                  <module.icon className={cn(
                    "w-5 h-5",
                    module.color === 'amber' ? "text-amber-400" : "text-purple-400"
                  )} />
                </div>
                <div>
                  <h3 className={cn(
                    "font-semibold",
                    activeModule === module.id ? "text-white" : "text-gray-300"
                  )}>
                    {module.title}
                  </h3>
                  <p className="text-xs text-gray-500">{module.subtitle}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
        
        {/* Module Content */}
        <AnimatePresence mode="wait">
          {activeModule === 'recommend' ? (
            <Part1RecommendTraining key="recommend" />
          ) : (
            <Part2PromoteTraining key="promote" />
          )}
        </AnimatePresence>
        
        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-12 text-center"
        >
          <p className="text-gray-400 mb-4">Ready to start earning?</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/ai-besty-link">
              <Button className="bg-gradient-to-r from-amber-500 to-orange-600 text-white font-bold">
                <Award className="w-4 h-4 mr-2" /> Get Your AI Besty Link
              </Button>
            </Link>
            <Link to="/affiliate/marketplace">
              <Button variant="outline" className="border-purple-500/30 text-purple-400">
                <Megaphone className="w-4 h-4 mr-2" /> Browse Affiliate Marketplace
              </Button>
            </Link>
          </div>
        </motion.div>
      </div>
    </DashboardLayout>
  );
}
